/**
 * Sanction — Online Only Badge
 *
 * Install as a Shopify theme snippet and render via {% render 'online-only-badge' %}
 * in the product section.
 *
 * What it does:
 *   1. On product pages, queries the Storefront API for per-variant
 *      store-location availability and purchasability.
 *   2. When the selected variant has zero stock at the retail store
 *      BUT is still purchasable (warehouse has stock), shows an
 *      "Online Only" badge on the page.
 *   3. For Online Only variants, overrides Shopify's "Pickup currently
 *      unavailable" text to show "Usually ready in 24 hours" (warehouse
 *      transfer).
 *
 * Requirements:
 *   - A Storefront API access token with the
 *     `unauthenticated_read_product_pickup_locations` AND
 *     `unauthenticated_read_product_inventory` scopes.
 *   - Store pickup enabled in Shopify admin (Settings > Shipping > Local
 *     pickup) so `storeAvailability` data is populated.
 */

(function () {
  'use strict';

  // ─── Configuration ──────────────────────────────────────────────
  var SANCTION_OO_CONFIG = {
    storefrontToken: '85d23c7453cf7964ec58c9f3ada2586c',
    shopDomain: 'sanction.myshopify.com',
    storeLocationName: 'Sanction World Headquarters',
    apiVersion: '2025-01',
  };
  // ─── End Configuration ──────────────────────────────────────────

  // ── Guards ──────────────────────────────────────────────────────
  var pathname = window.location.pathname;
  var productMatch = pathname.match(/\/products\/([^/?#]+)/);
  if (!productMatch) return;

  var productHandle = productMatch[1];

  // ── State ───────────────────────────────────────────────────────
  // { variantId: { storeAvailable: boolean, purchasable: boolean } }
  var availabilityMap = {};
  var dataLoaded = false;
  var badgeElement = null;
  var pickupObserver = null;
  var applyingPickupOverride = false;

  // ── Storefront API Query ────────────────────────────────────────
  var QUERY = [
    'query ProductStoreAvailability($handle: String!) {',
    '  product(handle: $handle) {',
    '    variants(first: 100) {',
    '      edges {',
    '        node {',
    '          id',
    '          title',
    '          availableForSale',
    '          storeAvailability(first: 10) {',
    '            edges {',
    '              node {',
    '                available',
    '                quantityAvailable',
    '                location {',
    '                  name',
    '                }',
    '              }',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  // ── Helpers ─────────────────────────────────────────────────────

  function numericId(gid) {
    var parts = gid.split('/');
    return parts[parts.length - 1];
  }

  function getSelectedVariantId() {
    var params = new URLSearchParams(window.location.search);
    var fromUrl = params.get('variant');
    if (fromUrl) return fromUrl;

    if (
      window.ShopifyAnalytics &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.selectedVariantId
    ) {
      return String(window.ShopifyAnalytics.meta.selectedVariantId);
    }

    try {
      var productJson = document.querySelector('[data-product-json], script[type="application/json"][data-product-json]');
      if (productJson) {
        var data = JSON.parse(productJson.textContent);
        if (data && data.variants && data.variants.length) {
          return String(data.variants[0].id);
        }
      }
    } catch (_) {}

    var keys = Object.keys(availabilityMap);
    return keys.length ? keys[0] : null;
  }

  function isOnlineOnly(variantId) {
    if (!dataLoaded || !variantId) return false;
    var info = availabilityMap[variantId];
    if (!info) return false;
    // Online Only = not available at store BUT still purchasable (warehouse has stock)
    return !info.storeAvailable && info.purchasable;
  }

  function isCurrentVariantOnlineOnly() {
    return isOnlineOnly(getSelectedVariantId());
  }

  // ── Badge Element ───────────────────────────────────────────────

  function createBadge() {
    var el = document.createElement('div');
    el.id = 'sanction-oo-badge';
    el.setAttribute('aria-live', 'polite');
    el.style.cssText = [
      'display: none',
      'margin: 8px 0',
      'padding: 6px 12px',
      'background: #5433EC',
      'color: #ffffff',
      'border-radius: 9999px',
      'font-size: 13px',
      'font-weight: 600',
      'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      'line-height: 1',
      'width: fit-content',
      'letter-spacing: 0.02em',
    ].join('; ');
    el.textContent = 'Online Only';
    return el;
  }

  function insertBadge() {
    if (badgeElement) return;

    badgeElement = createBadge();

    var selectors = [
      '.option-selector',
      'product-form .product-form__buttons',
      '.product-form__buttons',
      '.product-form__cart-submit',
      '.product-single__cart-submit',
      'form[action="/cart/add"] [type="submit"]',
      'form[action*="/cart/add"] button[name="add"]',
      '.product-form',
      'form[action="/cart/add"]',
    ];

    for (var i = 0; i < selectors.length; i++) {
      var target = document.querySelector(selectors[i]);
      if (target) {
        if (selectors[i] === '.option-selector') {
          var all = document.querySelectorAll(selectors[i]);
          var last = all[all.length - 1];
          last.insertAdjacentElement('afterend', badgeElement);
        } else {
          target.parentNode.insertBefore(badgeElement, target);
        }
        return;
      }
    }

    document.body.appendChild(badgeElement);
  }

  function updateBadge() {
    if (badgeElement && !document.contains(badgeElement)) {
      badgeElement = null;
    }
    if (!badgeElement) {
      insertBadge();
    }
    if (!badgeElement) return;
    if (isCurrentVariantOnlineOnly()) {
      badgeElement.style.display = 'inline-flex';
    } else {
      badgeElement.style.display = 'none';
    }
  }

  // ── Pickup Availability Override ─────────────────────────────────

  function applyPickupOverride() {
    if (applyingPickupOverride) return;
    applyingPickupOverride = true;

    try {
      var pickupEl = document.querySelector('pickup-availability');
      if (!pickupEl) return;

      if (!isCurrentVariantOnlineOnly()) return;

      // Find the status container (theme uses .pickup-status)
      var statusEl = pickupEl.querySelector('.pickup-status');
      if (!statusEl) return;

      var text = statusEl.textContent || '';
      // Only override if Shopify is showing "unavailable" (which it will for 0 store stock)
      if (text.indexOf('navailable') === -1) return;

      // Swap icon: change error styling to success
      var iconDiv = statusEl.querySelector('.pickup-icon');
      if (iconDiv) {
        iconDiv.classList.remove('text-error-text');
        iconDiv.classList.add('text-success-text');
        iconDiv.innerHTML = '<svg class="icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false" role="presentation"><path d="m16.558 4.8.884.884-9.959 9.958-4.925-4.925.884-.884 4.041 4.041z"/></svg>';
      }

      // Find the content div (sibling of icon div)
      var contentDiv = iconDiv ? iconDiv.nextElementSibling : null;
      if (!contentDiv) return;

      // Update the title text
      var titleP = contentDiv.querySelector('p');
      if (!titleP) return;
      titleP.innerHTML = 'Pickup available at <strong>' + SANCTION_OO_CONFIG.storeLocationName + '</strong>';

      // Remove any existing modal-opener (e.g. "check other stores")
      var existingOpener = contentDiv.querySelector('modal-opener');
      if (existingOpener) existingOpener.remove();

      // Add "Usually ready in 24 hours" subtitle
      var subtitleP = contentDiv.querySelector('.sanction-oo-pickup-subtitle');
      if (!subtitleP) {
        subtitleP = document.createElement('p');
        subtitleP.className = 'mb-0 text-sm sanction-oo-pickup-subtitle';
        subtitleP.textContent = 'Usually ready in 24 hours';
        titleP.insertAdjacentElement('afterend', subtitleP);
      }

      // Add "View store information" link
      var viewLink = contentDiv.querySelector('.sanction-oo-pickup-link');
      if (!viewLink) {
        viewLink = document.createElement('button');
        viewLink.className = 'link mt-2 text-sm sanction-oo-pickup-link';
        viewLink.textContent = 'View store information';
        viewLink.addEventListener('click', function () {
          var drawer = document.querySelector('.js-pickup-drawer');
          if (drawer && drawer.open) {
            applyDrawerOverride();
            drawer.open(viewLink);
          }
        });
        contentDiv.appendChild(viewLink);
      }
    } finally {
      applyingPickupOverride = false;
    }
  }

  function applyDrawerOverride() {
    var drawer = document.querySelector('.js-pickup-drawer');
    if (!drawer) return;

    var listItems = drawer.querySelectorAll('li');
    for (var i = 0; i < listItems.length; i++) {
      var li = listItems[i];
      var heading = li.querySelector('[role="heading"]');
      if (!heading || heading.textContent.trim() !== SANCTION_OO_CONFIG.storeLocationName) continue;

      var iconDiv = li.querySelector('.pickup-icon');
      if (iconDiv) {
        iconDiv.classList.remove('text-error-text');
        iconDiv.classList.add('text-success-text');
        iconDiv.innerHTML = '<svg class="icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false" role="presentation"><path d="m16.558 4.8.884.884-9.959 9.958-4.925-4.925.884-.884 4.041 4.041z"/></svg>';
      }

      var statusP = li.querySelector('.flex p');
      if (statusP) {
        statusP.textContent = 'Pickup available, usually ready in 24 hours';
      }
      break;
    }
  }

  function setupPickupObserver() {
    // Observe a parent that persists across variant changes
    var container = document.querySelector('[data-dynamic-variant-content$="-pickup-availability"]');
    if (!container || pickupObserver) return;

    pickupObserver = new MutationObserver(function () {
      if (applyingPickupOverride) return;
      // Let Shopify finish rendering before we override
      requestAnimationFrame(function () {
        applyPickupOverride();
      });
    });

    pickupObserver.observe(container, { childList: true, subtree: true });
  }

  // ── Variant Change Listener ─────────────────────────────────────
  // NOTE: Both online-only-badge.js and preorder-badge.js monkey-patch
  // history.pushState/replaceState. Script load order matters (defer
  // guarantees document order). Each wraps the previous wrapper, forming
  // a call chain. Both callbacks fire on every variant change.

  function watchVariantChanges() {
    var origPushState = history.pushState;
    var origReplaceState = history.replaceState;

    history.pushState = function () {
      origPushState.apply(this, arguments);
      onVariantChange();
    };

    history.replaceState = function () {
      origReplaceState.apply(this, arguments);
      onVariantChange();
    };

    window.addEventListener('popstate', onVariantChange);

    document.addEventListener(
      'change',
      function (e) {
        var target = e.target;
        if (!target) return;
        var name = (target.name || '').toLowerCase();
        var id = (target.id || '').toLowerCase();
        if (
          name.includes('variant') ||
          name.includes('option') ||
          name.includes('id') ||
          id.includes('variant') ||
          id.includes('option')
        ) {
          setTimeout(onVariantChange, 50);
        }
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        var target = e.target;
        if (!target) return;
        if (target.closest('.option-selector, .variant-input, [data-option], .swatch')) {
          setTimeout(onVariantChange, 100);
        }
      },
      true
    );

    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () {
        insertBadge();
        onVariantChange();
      }, 100);
    });

    document.addEventListener('variant:changed', onVariantChange);
    document.addEventListener('variantChange', onVariantChange);
  }

  function onVariantChange() {
    var vid = getSelectedVariantId();
    var info = vid ? availabilityMap[vid] : null;
    updateBadge();
    // Pickup override runs via MutationObserver when Shopify updates the DOM,
    // but also apply directly with retries in case the section fetch is slow
    setTimeout(applyPickupOverride, 300);
    setTimeout(applyPickupOverride, 800);
  }

  // ── Storefront API Fetch ────────────────────────────────────────

  function fetchAvailability() {
    var endpoint =
      'https://' +
      SANCTION_OO_CONFIG.shopDomain +
      '/api/' +
      SANCTION_OO_CONFIG.apiVersion +
      '/graphql.json';

    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SANCTION_OO_CONFIG.storefrontToken,
      },
      body: JSON.stringify({
        query: QUERY,
        variables: { handle: productHandle },
      }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Storefront API error: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.data || !data.data.product) {
          throw new Error('Product not found: ' + productHandle);
        }

        var variants = data.data.product.variants.edges;
        var storeName = SANCTION_OO_CONFIG.storeLocationName;

        var onlineOnlyCount = 0;
        for (var i = 0; i < variants.length; i++) {
          var variant = variants[i].node;
          var varId = numericId(variant.id);
          var storeLocations = variant.storeAvailability.edges;

          var storeQtyAtStore = 0;
          var storeLocationFound = false;

          for (var j = 0; j < storeLocations.length; j++) {
            var loc = storeLocations[j].node;
            if (loc.location.name === storeName) {
              storeQtyAtStore = loc.quantityAvailable;
              storeLocationFound = true;
              break;
            }
          }

          // Store is "available" only when it has on-hand stock > 0.
          // Absence from the response and presence with quantity 0 are
          // both treated as online-only — pickup routing from warehouse
          // would otherwise make `available` report true at a 0-qty store.
          var storeAvailable = storeLocationFound && storeQtyAtStore > 0;
          availabilityMap[varId] = {
            storeAvailable: storeAvailable,
            purchasable: variant.availableForSale,
          };

          if (!storeAvailable && variant.availableForSale) onlineOnlyCount++;
        }
        console.log('[Sanction OO] ' + productHandle + ': ' + onlineOnlyCount + '/' + variants.length + ' variants online-only');

        dataLoaded = true;
      });
  }

  // ── Initialization ──────────────────────────────────────────────

  function init() {
    fetchAvailability()
      .then(function () {
        insertBadge();
        updateBadge();
        watchVariantChanges();
        setupPickupObserver();
        // Initial pickup override after a delay (Shopify may still be loading)
        setTimeout(applyPickupOverride, 500);
      })
      .catch(function (err) {
        console.warn('[Sanction OO] Failed to load availability data:', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
